import {
  DesignTokenSize,
  ThemeDefinition,
  styled,
} from '@volkswagen-onehub/components-core';
import { enumIncludes, objectKeys } from '@/modules/utils/object';
import {
  convertBreakpointsToMediaQueries,
  jssToCss,
} from '@/modules/utils/themeUtils';
import type { BreakpointsProps } from '@/types';
import type { BoxBaseProps } from './Box';

interface StyledBoxProps extends BoxBaseProps, BreakpointsProps<BoxBaseProps> {
  theme: ThemeDefinition;
}

enum SizeKeys {
  padding,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  margin,
  marginLeft,
  marginRight,
  marginTop,
  marginBottom,
  gap,
  gutter,
}

const VALID_PROPS = [
  'position',
  'zIndex',
  'top',
  'left',
  'right',
  'bottom',
  'width',
  'minWidth',
  'maxWidth',
  'height',
  'minHeight',
  'maxHeight',
  'display',
  'alignItems',
  'justifyContent',
  'flexGrow',
  'gap',
  'flexDirection',
  'flexBasis',
  'flexShrink',
  'px',
  'py',
  'p',
  'pr',
  'pt',
  'pb',
  'pl',
  'padding',
  'paddingLeft',
  'paddingBottom',
  'paddingRight',
  'paddingTop',
  'mx',
  'my',
  'm',
  'mr',
  'mt',
  'mb',
  'ml',
  'margin',
  'marginLeft',
  'marginBottom',
  'marginRight',
  'marginTop',
  'cursor',
  'border',
  'borderRadius',
  'color',
  'b560',
  'b960',
  'b1280',
  'b1600',
  'b1920',
  'b2560',
];

//Gets the size from the theme based on value e.g. static300
const getSize = (theme: ThemeDefinition, size?: DesignTokenSize) => {
  if (size === undefined) return '';

  return theme.size[size];
};

//Transforms properties with a value of e.g. static300
export const transformSizes = (
  theme: ThemeDefinition,
  props?: Partial<BoxBaseProps>
) => {
  if (!props) return '';

  const styles: {
    [key: string]: string | number | DesignTokenSize | undefined;
  } = {};
  const keys = objectKeys(props);

  keys.forEach((key) => {
    if (enumIncludes(SizeKeys, key)) {
      styles[key] = getSize(theme, props[key] as DesignTokenSize);
    } else {
      (styles as any)[key] = props[key];
    }
  });

  return styles;
};

//Transforms alias properties
const transformAliases = ({
  px,
  py,
  p,
  pr,
  pt,
  pb,
  pl,
  padding,
  paddingLeft,
  paddingBottom,
  paddingRight,
  paddingTop,
  mx,
  my,
  m,
  mr,
  mt,
  mb,
  ml,
  margin,
  marginLeft,
  marginBottom,
  marginRight,
  marginTop,
  ...restProps
}: Partial<BoxBaseProps>) => ({
  padding: p || padding,
  paddingLeft: px || pl || paddingLeft,
  paddingBottom: py || pb || paddingBottom,
  paddingRight: px || pr || paddingRight,
  paddingTop: py || pt || paddingTop,
  margin: m || margin,
  marginLeft: mx || ml || marginLeft,
  marginBottom: my || mb || marginBottom,
  marginRight: mx || mr || marginRight,
  marginTop: my || mt || marginTop,
  ...restProps,
});

//Strips out things like children where there are typing issues
const filterValidProps = (props: Partial<BoxBaseProps> | undefined) => {
  const propsToFilter = { ...props };

  if (props) {
    Object.keys(props).forEach((key: string) => {
      if (!VALID_PROPS.includes(key))
        delete propsToFilter[key as keyof BoxBaseProps];
    });
  }

  return propsToFilter;
};

export const StyledBox = styled('div').withConfig({
  shouldForwardProp: (prop) => !VALID_PROPS.includes(prop),
})(
  ({
    b560,
    b960,
    b1280,
    b1600,
    b1920,
    b2560,
    theme,
    ...restProps
  }: StyledBoxProps) => `
    ${jssToCss(
      transformSizes(theme, transformAliases(filterValidProps(restProps)))
    )}

    ${convertBreakpointsToMediaQueries({
      b560: transformSizes(theme, transformAliases(filterValidProps(b560))),
      b960: transformSizes(theme, transformAliases(filterValidProps(b960))),
      b1280: transformSizes(theme, transformAliases(filterValidProps(b1280))),
      b1600: transformSizes(theme, transformAliases(filterValidProps(b1600))),
      b1920: transformSizes(theme, transformAliases(filterValidProps(b1920))),
      b2560: transformSizes(theme, transformAliases(filterValidProps(b2560))),
    })};
`
);
